import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const steps = [
  {
    num: '01', title: 'Discover',
    copy: [
      'We begin by understanding the brand.',
      'We examine current positioning, audience, visuals, strengths, gaps, and opportunities.',
      'This stage allows us to identify what exists and what needs refinement.',
    ],
  },
  {
    num: '02', title: 'Clarify',
    copy: [
      'Once we understand the landscape, we create clarity.',
      'This phase focuses on messaging, identity, voice, and direction.',
      'We establish how the brand should be perceived moving forward.',
    ],
  },
  {
    num: '03', title: 'Direct',
    copy: [
      'Creative direction becomes the blueprint.',
      'We define visual references, content structure, storytelling, aesthetics, and presentation.',
      'This ensures every future touchpoint feels aligned.',
    ],
  },
  {
    num: '04', title: 'Produce',
    copy: [
      'We bring the vision to life.',
      'Photography, video, campaigns, social content, and brand assets are executed with strategy in mind.',
    ],
  },
  {
    num: '05', title: 'Refine',
    copy: [
      'After launch or production, we assess.',
      'We identify what resonates, strengthen consistency, and refine based on performance and perception.',
    ],
  },
  {
    num: '06', title: 'Expand',
    copy: [
      'Strong brands evolve.',
      'We support future growth through ongoing direction, campaigns, launches, partnerships, and long-term creative support.',
    ],
  },
];

export default function MuseMethodPage() {
  return (
    <div className="pt-44" data-testid="method-page">
      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="method-hero">
        <div className="container-main">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">The Muse Method</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-10">
              Our Process Is Designed To Build Intentional Brands.
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Every strong brand begins with clarity. Muse follows a structured process designed to create intentional results. Our methodology allows us to move beyond aesthetics and build brands with depth, consistency, and purpose.
            </motion.p>

            <motion.div variants={fadeInUp} className="mt-12">
              <img src="/images/method-hero-cloche.png" alt="Brand Desire, Engineered" className="w-full max-w-lg mx-auto h-auto object-contain" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* Steps */}
      <section className="py-16 md:py-24" data-testid="method-steps">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-3xl mx-auto divide-y divide-border">
            {steps.map((step, i) => (
              <motion.div key={i} variants={fadeInUp} className="py-12 first:pt-0 last:pb-0" data-testid={`method-step-${i}`}>
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="font-serif text-5xl md:text-6xl text-butter/40 font-medium">{step.num}</span>
                  <h2 className="font-serif text-2xl md:text-3xl font-medium">{step.title}</h2>
                </div>
                <div className="space-y-4 pl-0 md:pl-20">
                  {step.copy.map((p, j) => (
                    <p key={j} className="text-muted-foreground text-base md:text-lg leading-relaxed">{p}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-foreground" data-testid="method-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-background mb-10">
              Luxury Begins With Clarity.
            </motion.h2>
            <motion.div variants={fadeInUp}>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="method-cta-btn">
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
